import { api} from '../utils/variables';

// Get auth token from localStorage. When there's no token (an anonymous or
// guest visitor), omit Authorization entirely rather than sending the
// literal string "Bearer null" — a malformed bearer token gets rejected by
// the backend as a bad credential (401) even on endpoints that are meant to
// allow anonymous access, like /payments/api/products/ (the plans list the
// public landing page's PlansTable already fetches with no auth header at
// all and it works fine). Endpoints that genuinely require a real account
// still 401 correctly on a plain unauthenticated request — this only fixes
// the ones that don't.
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
};

/**
 * User Profile API Service
 * Centralized API calls for user profile related functionality
 */

export const userProfileApi = {
  /**
   * Fetch profile details from /user/get-profile
   * @returns {Promise<Object>} { uid, email, name, is_active }
   */
  fetchProfileDetails: async () => {
    try {
      const response = await fetch(`${api}/user/get-profile`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching profile details:', error);
      throw error;
    }
  },
  /**
   * Fetch all available plans/products
   * @returns {Promise<Object>} Plans data
   */
  fetchPlans: async () => {
    try {
      const response = await fetch(`${api}/payments/api/products/`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.data || !Array.isArray(result.data)) {
        throw new Error('Invalid response format');
      }

      return result;
    } catch (error) {
      console.error('Error fetching plans:', error);
      throw error;
    }
  },

  /**
   * Fetch current user's plan
   * @returns {Promise<Object>} Current plan data
   */
  fetchCurrentPlan: async () => {
    try {
      const response = await fetch(`${api}/payments/current_plan`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.data) {
        throw new Error('Invalid response format');
      }

      return result;
    } catch (error) {
      console.error('Error fetching current plan:', error);
      throw error;
    }
  },

  /**
   * Create payment session for plan purchase
   * @param {number} productId - The product ID to purchase
   * @returns {Promise<Object>} Payment session data
   */
  createPaymentSession: async (productId) => {
    try {
      const response = await fetch(`${api}/payments/api/payments/create_session/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ product_id: productId })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      if (!data.session_id) {
        throw new Error('No session ID in response');
      }

      return data;
    } catch (error) {
      console.error('Error creating payment session:', error);
      throw error;
    }
  },

  /**
   * Cancel user subscription
   * @param {string} subscriptionId - The subscription ID to cancel
   * @returns {Promise<Object>} Cancellation result
   */
  cancelSubscription: async (subscriptionId) => {
    try {
      const response = await fetch(`${api}/payments/api/subscriptions.cancel/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ subscription_id: subscriptionId })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      throw error;
    }
  },

  /**
   * Get proration preview for upgrading a subscription to a new product
   * @param {string} subscriptionId - Current subscription identifier
   * @param {number|string} productId - Target product/plan id
   * @returns {Promise<Object>} Preview data with lines and total_amount_due
   */
  getUpgradePreview: async (subscriptionId, productId) => {
    try {
      const response = await fetch(`${api}/payments/api/payments/get_preview_for_upgrade/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          subscription_id: subscriptionId,
          product_id: productId,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching upgrade preview:', error);
      throw error;
    }
  },

  /**
   * Update user profile information
   * @param {Object} profileData - User profile data to update
   * @returns {Promise<Object>} Updated profile data
   */
  updateProfile: async (profileData) => {
    try {
      const response = await fetch(`${api}/user/update-profile`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(profileData)
      });

      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result; // { message: "Profile updated successfully" }
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  },

  /**
   * Fetch user profile information
   * @returns {Promise<Object>} User profile data
   */
  fetchProfile: async () => {
    try {
      const response = await fetch(`${api}/user/get-profile`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching profile:', error);
      throw error;
    }
  },

  /**
   * Change user password
   * @param {Object} passwordData - Old and new password
   * @returns {Promise<Object>} Password change result
   */
  changePassword: async ({ current_password, new_password }) => {
    try {
      const response = await fetch(`${api}/user/change-password`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ current_password, new_password })
      });

      const contentType = response.headers.get('content-type') || ''

      if (!response.ok) {
        if (contentType.includes('application/json')) {
          const errJson = await response.json()
          throw new Error(errJson?.error || `HTTP ${response.status}`)
        } else {
          const text = await response.text()
          throw new Error(text || `HTTP ${response.status}`)
        }
      }

      if (contentType.includes('application/json')) {
        return await response.json()
      }
      return { message: 'Password changed successfully' }
    } catch (error) {
      console.error('Error changing password:', error);
      throw error;
    }
  },

  /**
   * Upload profile picture
   * @param {File} file - Image file to upload
   * @returns {Promise<Object>} Upload result
   */
  uploadProfilePicture: async (file) => {
    try {
      const formData = new FormData();
      formData.append('profile_picture', file);

      const response = await fetch(`${api}/user/upload-picture/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      throw error;
    }
  },

  /**
   * Fetch billing history
   * @param {number} page - Page number for pagination
   * @returns {Promise<Object>} Billing history data
   */
  fetchBillingHistory: async (page = 1) => {
    try {
      const response = await fetch(`${api}/payments/api/payments?page=${page}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching billing history:', error);
      throw error;
    }
  },

  /**
   * Get invoice URL for a payment
   * @param {string} paymentId - The payment ID to get invoice for
   * @returns {Promise<Object>} Invoice data with URL
   */
  getInvoice: async (paymentId) => {
    try {
      const response = await fetch(`${api}/payments/api/payments/get_invoice?payment_id=${paymentId}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error getting invoice:', error);
      throw error;
    }
  }
};

export default userProfileApi; 